import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, RefreshCw, ExternalLink, Radio, Power } from "lucide-react";
import { IANA_TIMEZONES } from "@/lib/timezones";

function PublicXmltvCard({ tenant, refresh }) {
  const [urlInfo, setUrlInfo] = useState(null);
  const [ttlHours, setTtlHours] = useState(24 * 30);
  const [busy, setBusy] = useState(false);
  const enabled = tenant?.xmltv_public_enabled;
  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const loadUrl = async () => {
    if (!enabled) { setUrlInfo(null); return; }
    try {
      const r = await api.get("/tenants/current/xmltv/url", { params: { ttl_hours: ttlHours, all_langs: true } });
      setUrlInfo(r.data);
    } catch { /* noop */ }
  };
  useEffect(() => { loadUrl(); /* eslint-disable-next-line */ }, [enabled, ttlHours]);

  const toggle = async () => {
    setBusy(true);
    try {
      if (enabled) await api.post("/tenants/current/xmltv/disable");
      else await api.post("/tenants/current/xmltv/enable");
      await refresh();
      toast.success(enabled ? "Public XMLTV disabled" : "Public XMLTV enabled · secret rotated");
    } catch { toast.error("Toggle failed"); }
    setBusy(false);
  };
  const rotate = async () => {
    setBusy(true);
    try {
      await api.post("/tenants/current/xmltv/enable"); // re-enable = rotate secret
      await refresh();
      await loadUrl();
      toast.success("HMAC secret rotated · new URL");
    } catch { toast.error("Rotate failed"); }
    setBusy(false);
  };
  const fullUrl = urlInfo ? `${backend}${urlInfo.url}` : "";
  const copyUrl = () => { navigator.clipboard.writeText(fullUrl); toast.success("URL copied"); };

  return (
    <div className="panel p-6 space-y-3" data-testid="xmltv-public-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-emerald-400" />
          <div>
            <div className="font-display font-bold">Public XMLTV feed</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              signed URL · unauthenticated · HMAC-SHA256
            </div>
          </div>
        </div>
        <Button
          onClick={toggle}
          disabled={busy}
          data-testid="xmltv-toggle-btn"
          variant="outline"
          className={`rounded-sm ${enabled ? "border-emerald-500/60 text-emerald-400" : "border-zinc-700 text-zinc-300"}`}
        >
          <Power className="w-3.5 h-3.5 mr-1" /> {enabled ? "Disable" : "Enable"}
        </Button>
      </div>

      {enabled && urlInfo && (
        <>
          <div className="grid grid-cols-[1fr_120px_auto] gap-2 items-end">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Signed URL</Label>
              <Input
                value={fullUrl}
                readOnly
                onFocus={(e) => e.target.select()}
                className="mt-1 bg-black border-zinc-800 font-mono text-[10px]"
                data-testid="xmltv-url-input"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">TTL hours</Label>
              <Input
                type="number" min="1" step="1" value={ttlHours}
                onChange={(e) => setTtlHours(Number(e.target.value))}
                className="mt-1 bg-black border-zinc-800 font-mono text-xs"
                data-testid="xmltv-ttl-input"
              />
            </div>
            <Button onClick={copyUrl} className="rounded-sm tenant-btn" data-testid="xmltv-copy-btn">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy
            </Button>
          </div>
          <div className="flex items-center justify-between text-[11px] font-mono text-zinc-400">
            <span>
              Expires <span className="text-zinc-200">{new Date(urlInfo.expires_at).toLocaleString([], { hour12: false })}</span>
              {" · TTL "}{urlInfo.ttl_hours} h
            </span>
            <div className="flex items-center gap-2">
              <a href={fullUrl} target="_blank" rel="noreferrer" className="hover:text-emerald-300 flex items-center gap-1" data-testid="xmltv-open-link">
                <ExternalLink className="w-3 h-3" /> Open
              </a>
              <button onClick={rotate} disabled={busy} className="hover:text-red-300 flex items-center gap-1" data-testid="xmltv-rotate-btn">
                <RefreshCw className="w-3 h-3" /> Rotate secret
              </button>
            </div>
          </div>
          <div className="border border-zinc-800 rounded-sm bg-black/30 p-2 font-mono text-[10px] text-zinc-500 leading-relaxed">
            Rotating the secret invalidates every previously issued URL. Use TTL as
            short as your consumer allows and rotate on a schedule for zero-trust
            distribution. The feed itself covers <span className="text-zinc-300">all channels</span>
            {" "}on this tenant and emits multi-language <span className="text-emerald-400">&lt;title lang=&quot;…&quot;&gt;</span> elements.
          </div>
        </>
      )}
      {!enabled && (
        <div className="text-xs text-zinc-500 italic">
          Public XMLTV is disabled. Enable to generate a signed URL you can hand to
          any set-top box, EPG aggregator, or MVPD.
        </div>
      )}
    </div>
  );
}

export default function TenantSettings() {
  const { tenant, refresh } = useAuth();
  const [form, setForm] = useState({
    name: "",
    contact_name: "",
    contact_email: "",
    timezone: "UTC",
    custom_domain: "",
    default_loudnorm_target: -23,
    branding: {
      primary_color: "#3B82F6",
      accent_color: "",
      product_name: "MOXYPLAY",
      tagline: "",
      logo_url: "",
      favicon_url: "",
      support_email: "",
      website: "",
    },
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (tenant) {
      setForm({
        name: tenant.name || "",
        contact_name: tenant.contact_name || "",
        contact_email: tenant.contact_email || "",
        timezone: tenant.timezone || "UTC",
        custom_domain: tenant.custom_domain || "",
        default_loudnorm_target: tenant.default_loudnorm_target || -23,
        branding: {
          primary_color: tenant.branding?.primary_color || "#3B82F6",
          accent_color: tenant.branding?.accent_color || "",
          product_name: tenant.branding?.product_name || "MOXYPLAY",
          tagline: tenant.branding?.tagline || "",
          logo_url: tenant.branding?.logo_url || "",
          favicon_url: tenant.branding?.favicon_url || "",
          support_email: tenant.branding?.support_email || "",
          website: tenant.branding?.website || "",
        },
      });
    }
  }, [tenant]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/tenants/current", form);
      toast.success("Tenant updated");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const setBrand = (k, v) => setForm((f) => ({ ...f, branding: { ...f.branding, [k]: v } }));

  return (
    <div className="p-4 max-w-3xl space-y-4">
      <div className="mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">§ Tenant / White-label</div>
        <h1 className="font-display font-bold text-2xl mt-1">Brand settings · Public feeds</h1>
      </div>

      <div className="panel p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Organization name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="mt-1 rounded-sm"
              data-testid="tenant-name-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Timezone</Label>
            <select
              value={form.timezone}
              onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
              className="mt-1 w-full rounded-sm bg-background border border-border text-sm px-2 py-2 font-mono"
              data-testid="tenant-timezone"
            >
              {IANA_TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">House loudness target (LUFS)</Label>
            <select
              value={form.default_loudnorm_target}
              onChange={(e) => setForm((f) => ({ ...f, default_loudnorm_target: parseInt(e.target.value, 10) }))}
              className="mt-1 w-full rounded-sm bg-background border border-border text-sm px-2 py-2 font-mono"
              data-testid="tenant-loudness-default"
            >
              <option value={-23}>−23 LUFS · EBU R128</option>
              <option value={-24}>−24 LUFS · ATSC A/85</option>
              <option value={-16}>−16 LUFS · Streaming</option>
            </select>
            <div className="text-[10px] font-mono text-muted-foreground mt-1">New CDN destinations start from this target.</div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Contact name</Label>
            <Input
              value={form.contact_name}
              onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
              className="mt-1 rounded-sm"
              data-testid="tenant-contact-name"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Contact email</Label>
            <Input
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
              className="mt-1 rounded-sm"
              data-testid="tenant-contact-email"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Custom domain (optional)</Label>
            <Input
              value={form.custom_domain}
              onChange={(e) => setForm((f) => ({ ...f, custom_domain: e.target.value }))}
              className="mt-1 rounded-sm font-mono"
              placeholder="play.your-brand.com"
              data-testid="tenant-custom-domain"
            />
          </div>
        </div>

        <div className="border-t border-border pt-4 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Brand identity</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Product name</Label>
              <Input
                value={form.branding.product_name}
                onChange={(e) => setBrand("product_name", e.target.value)}
                className="mt-1 rounded-sm"
                data-testid="tenant-product-name"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Tagline</Label>
              <Input
                value={form.branding.tagline}
                onChange={(e) => setBrand("tagline", e.target.value)}
                className="mt-1 rounded-sm"
                data-testid="tenant-tagline"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Support email</Label>
              <Input
                type="email"
                value={form.branding.support_email}
                onChange={(e) => setBrand("support_email", e.target.value)}
                className="mt-1 rounded-sm"
                placeholder="help@your-brand.com"
                data-testid="tenant-support-email"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Marketing website</Label>
              <Input
                value={form.branding.website}
                onChange={(e) => setBrand("website", e.target.value)}
                className="mt-1 rounded-sm"
                placeholder="https://your-brand.com"
                data-testid="tenant-website"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Primary color</Label>
              <div className="flex gap-2 items-center mt-1">
                <input
                  type="color"
                  value={form.branding.primary_color || "#3B82F6"}
                  onChange={(e) => setBrand("primary_color", e.target.value)}
                  className="h-10 w-16 border border-border rounded-sm cursor-pointer"
                  data-testid="tenant-color-picker"
                />
                <Input
                  value={form.branding.primary_color}
                  onChange={(e) => setBrand("primary_color", e.target.value)}
                  className="rounded-sm font-mono"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Accent color (optional)</Label>
              <div className="flex gap-2 items-center mt-1">
                <input
                  type="color"
                  value={form.branding.accent_color || "#22D3EE"}
                  onChange={(e) => setBrand("accent_color", e.target.value)}
                  className="h-10 w-16 border border-border rounded-sm cursor-pointer"
                  data-testid="tenant-accent-picker"
                />
                <Input
                  value={form.branding.accent_color}
                  onChange={(e) => setBrand("accent_color", e.target.value)}
                  className="rounded-sm font-mono"
                  placeholder="#22D3EE"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Logo URL</Label>
              <Input
                value={form.branding.logo_url}
                onChange={(e) => setBrand("logo_url", e.target.value)}
                className="mt-1 rounded-sm"
                placeholder="https://…/logo.png"
                data-testid="tenant-logo-url"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Favicon URL</Label>
              <Input
                value={form.branding.favicon_url}
                onChange={(e) => setBrand("favicon_url", e.target.value)}
                className="mt-1 rounded-sm"
                placeholder="https://…/favicon.ico"
                data-testid="tenant-favicon-url"
              />
            </div>
          </div>
        </div>

        <div className="pt-2 border-t border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Preview</div>
          <div className="flex items-center gap-3 p-3 border border-border rounded-sm bg-muted/20">
            {form.branding.logo_url ? (
              <img src={form.branding.logo_url} alt="" className="w-8 h-8 object-contain rounded-sm" />
            ) : (
              <div className="w-8 h-8 rounded-sm flex items-center justify-center text-white font-bold" style={{ background: form.branding.primary_color }}>
                {(form.branding.product_name || "?").slice(0, 2).toUpperCase()}
              </div>
            )}
            <div>
              <div className="font-display font-bold">{form.branding.product_name}</div>
              <div className="text-xs text-muted-foreground">{form.branding.tagline}</div>
            </div>
            <Button
              className="ml-auto tenant-btn rounded-sm"
              style={{ background: form.branding.primary_color }}
              data-testid="tenant-preview-btn"
            >
              Primary CTA
            </Button>
          </div>
        </div>
        <Button onClick={save} disabled={busy} className="tenant-btn rounded-sm" data-testid="tenant-save-btn">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <PublicXmltvCard tenant={tenant} refresh={refresh} />
    </div>
  );
}
